/**
 * Java 8 lexer and lexer type definitions.
 *
 * TOKEN ENUMS (TokenType)
 *
 * The lexer frequently needs the length of a (fixed-length) token, such as == or >>>. The tokens are therefore ordered
 * specifically in contiguous ranges of the same length, i.e., the order does matter here.
 */

import {isJavaIdentifierPart, isJavaIdentifierStart} from "./character_table";
import {parseHexFloat} from "./hexfloat";
import {replaceUnicodeEscapes, UnicodeUnescapedResult} from "./escapes";
import {cafebabeAssert, CompileError, JavaVersion, Span} from "./error";

/** Keyword, see JLS §3.9. Note that var, etc. are not part of Java 8. */
export enum Keyword {
	abstract = 0,
	continue,
	for,
	new,
	switch,
	assert,
	default,
	if,
	package,
	synchronized,
	boolean,
	do,
	goto,
	private,
	this,
	break,
	double,
	implements,
	protected,
	throw,
	byte,
	else,
	import,
	public,
	throws,
	case,
	enum,
	instanceof,
	return,
	transient,
	catch,
	extends,
	int,
	short,
	try,
	char,
	final,
	interface,
	static,
	void,
	class,
	finally,
	long,
	strictfp,
	volatile,
	const,
	float,
	native,
	super,
	while,
	_, // _ = 50
}

// Whether the given token type represents a keyword.
export function isKeyword(e: number): e is Keyword {
	return e >= 0 && e <= 50;
}

// List of all keywords and literals (true/false/null), as strings, to distinguish them from identifiers
const keywordOrLiteralSet = new Set(
	Object.keys(Keyword).filter(
		(k) => isNaN(Number(k)) /* filter out numeric keys */
	)
);
keywordOrLiteralSet.add("true");
keywordOrLiteralSet.add("false");
keywordOrLiteralSet.add("null");

function isStringKeywordOrLiteral(s: string, javaVersion: JavaVersion) {
	if (keywordOrLiteralSet.has(s)) {
		// _ was added in Java 9
		return !(s === "_" && javaVersion < 9);
	}
	return false;
}

/** See JLS §3.11. We organise by length to speed up token length calculations. */
export enum Separator {
	LParen = 51, // _ + 1
	RParen,
	LBrace,
	RBrace,
	LBracket,
	RBracket,
	Semicolon,
	Comma,
	Dot,
	At,
	// length 2
	ColonColon,
	// length 3
	Ellipsis, // Ellipsis = 62
}

// Whether the given token type represents a separator.
export function isSeparator(e: number): e is Separator {
	return e >= 51 && e <= 62;
}

/** See JLS §3.12. We organize by length to speed up token length calculations. */
export enum Operator {
	// Length 1
	Eq = 63, // Ellipsis + 1
	Gt,
	Lt,
	Not,
	Tilde,
	Question,
	Colon,
	Plus,
	Minus,
	Star,
	Slash,
	And,
	Or,
	Percent,
	Caret,
	// Length 2
	EqEq,
	GtEq,
	LtEq,
	NotEq,
	AndAnd,
	OrOr,
	PlusPlus,
	MinusMinus,
	CaretEq,
	LtLt,
	GtGt,
	PlusEq,
	MinusEq,
	StarEq,
	SlashEq,
	AndEq,
	OrEq,
	PercentEq,
	Arrow,
	// length 3
	LtLtEq,
	GtGtEq,
	GtGtGt,
	// length 4
	GtGtGtEq, // GtGtGtEq = 100
}

// Whether the given token type represents an operator.
export function isOperator(e: number): e is Operator {
	return e >= 63 && e <= 100;
}

// Partial mapping of operator or separator to string.
const FixedLengthTokenLookupTable: {[key: number]: string} = {
	[Operator.Eq]: "=",
	[Operator.Gt]: ">",
	[Operator.Lt]: "<",
	[Operator.Not]: "!",
	[Operator.Tilde]: "~",
	[Operator.Question]: "?",
	[Operator.Colon]: ":",
	[Operator.Plus]: "+",
	[Operator.Minus]: "-",
	[Operator.Star]: "*",
	[Operator.Slash]: "/",
	[Operator.And]: "&",
	[Operator.Or]: "|",
	[Operator.Percent]: "%",
	[Operator.Caret]: "^",
	[Operator.EqEq]: "==",
	[Operator.GtEq]: ">=",
	[Operator.LtEq]: "<=",
	[Operator.NotEq]: "!=",
	[Operator.AndAnd]: "&&",
	[Operator.OrOr]: "||",
	[Operator.PlusPlus]: "++",
	[Operator.MinusMinus]: "--",
	[Operator.CaretEq]: "^=",
	[Operator.LtLt]: "<<",
	[Operator.GtGt]: ">>",
	[Operator.PlusEq]: "+=",
	[Operator.MinusEq]: "-=",
	[Operator.StarEq]: "*=",
	[Operator.SlashEq]: "/=",
	[Operator.AndEq]: "&=",
	[Operator.OrEq]: "|=",
	[Operator.PercentEq]: "%=",
	[Operator.Arrow]: "->",
	[Operator.LtLtEq]: "<<=",
	[Operator.GtGtEq]: ">>=",
	[Operator.GtGtGt]: ">>>",
	[Operator.GtGtGtEq]: ">>>=",
	[Separator.LParen]: "(",
	[Separator.RParen]: ")",
	[Separator.LBrace]: "{",
	[Separator.RBrace]: "}",
	[Separator.LBracket]: "[",
	[Separator.RBracket]: "]",
	[Separator.Semicolon]: ";",
	[Separator.Comma]: ",",
	[Separator.Dot]: ".",
	[Separator.At]: "@",
	[Separator.ColonColon]: "::",
	[Separator.Ellipsis]: "...",
};

// Any operator which assigns to the left hand side.
export type AssignmentOrCompoundAssignment = TokenOfType<
	| Operator.Eq
	| Operator.PlusEq
	| Operator.CaretEq
	| Operator.GtGtGtEq
	| Operator.StarEq
	| Operator.MinusEq
	| Operator.GtGtEq
	| Operator.LtLtEq
	| Operator.NotEq
	| Operator.OrEq
	| Operator.AndEq
	| Operator.PercentEq
>;

export function isAssignmentOrCompoundAssignment(
	operator: Operator
): operator is AssignmentOrCompoundAssignment["type"] {
	return (
		operator === Operator.PlusEq ||
		operator === Operator.MinusEq ||
		operator === Operator.StarEq ||
		operator === Operator.SlashEq ||
		operator === Operator.AndEq ||
		operator === Operator.OrEq ||
		operator === Operator.CaretEq ||
		operator === Operator.PercentEq ||
		operator === Operator.LtLtEq ||
		operator === Operator.GtGtEq ||
		operator === Operator.GtGtGtEq ||
		operator === Operator.Eq
	);
}

// One of Java's eight primitive types.
export type PrimitiveKw =
	| Keyword.int
	| Keyword.short
	| Keyword.byte
	| Keyword.char
	| Keyword.long
	| Keyword.float
	| Keyword.double
	| Keyword.boolean;

export function isPrimitive(type: Token["type"]): type is PrimitiveKw {
	switch (type) {
		case Keyword.int:
		case Keyword.short:
		case Keyword.byte:
		case Keyword.long:
		case Keyword.double:
		case Keyword.float:
		case Keyword.char:
		case Keyword.boolean:
			return true;
	}
	return false;
}

/**
 * Miscellaneous token type.
 */
export enum Misc {
	// e.g. "ArrayList" or "a"
	identifier = 101, // GtGtGtEq + 1
	// e.g. "100" or "100L" (but not -100, which is parsed as Operator.Minus + integer)
	integer,
	// e.g. 0x1.0p-1 or 0.01
	floatLiteral,
	// e.g. 'c'
	characterLiteral,
	// e.g. "cowpox"
	string,
	null,
	// true or false
	booleanLiteral,
	comment,
	eof, // eof = 108

	erroneous, // erroneous = 109
}

// All possible values for the token type discriminant.
export type TokenType = Keyword | Separator | Operator | Misc;

// For a given token type, get the string corresponding to it.
export function tokenTypeToString(tt: TokenType) {
	if (isKeyword(tt)) return Keyword[tt];
	if (isSeparator(tt) || isOperator(tt))
		return FixedLengthTokenLookupTable[tt]!;
	return Misc[tt];
}

// For a given token of fixed length, return its length in bytes
export function tokenTypeToLength(tt: Operator | Separator): number {
	if (isOperator(tt))
		return (
			1 +
			+(tt >= Operator.EqEq) + // first length 2
			+(tt >= Operator.LtLtEq) + // first length 3
			+(tt === Operator.GtGtGtEq) // first (and only) length 4
		);
	return tt === Separator.Ellipsis ? 3 : tt === Separator.ColonColon ? 2 : 1;
}

/** e.g. System */
export type TokenIdentifier = {
	type: Misc.identifier;
	value: string;
	span: Span;
};

/** e.g. += */
export type TokenOperator = {
	type: Operator;
	span: Span;
};

/** e.g. ) */
export type TokenSeparator = {
	type: Separator;
	span: Span;
};

/** e.g. public. Keywords cannot be identifiers. */
export type TokenKeyword = {
	type: Keyword;
	span: Span;
};

/** Integer literal, like 0L */
export type IntegerLiteral = {
	type: Misc.integer;
	value: bigint;
	isLong: boolean;
	originalType: "decimal" | "hex" | "octal" | "binary";
	span: Span;
};

/** Floating point literal, like 0.0f or 0x1.8p-1 */
export type FloatingPointLiteral = {
	type: Misc.floatLiteral;
	value: number;
	isF32: boolean;
	isHexFloat: boolean;
	span: Span;
};

/** Character literal, like '\u1234' */
export type CharacterLiteral = {
	type: Misc.characterLiteral;
	value: string;
	span: Span;
};

/** String literal, like "abc" */
export type StringLiteral = {
	type: Misc.string;
	value: string;
	span: Span;
};

/** null */
export type NullLiteral = {
	type: Misc.null;
	span: Span;
};

/** true or false */
export type BooleanLiteral = {
	type: Misc.booleanLiteral;
	value: boolean;
	span: Span;
};

/** See section 3.10 */
export type TokenLiteral =
	| IntegerLiteral
	| FloatingPointLiteral
	| CharacterLiteral
	| StringLiteral
	| NullLiteral
	| BooleanLiteral;

/** Useful utility to specify a token is of a specific type. */
export type TokenOfType<T extends TokenType> = Token & {type: T};

// Technically, a comment isn't a token and is removed early, but we'll treat it as one for some purposes as we care
// about, for example, docstrings.

export type Comment = {
	type: Misc.comment;
	value: string; // includes the full thing, so // or /* */, etc.
	span: Span;
};

// Represents an erroneous token, which should not escape the lexer.
export type Erroneous = {
	type: Misc.erroneous;
	value: string;
};

export type TokenEof = {
	type: Misc.eof;
	span: Span;
};

/** A single token, excluding comments. */
export type Token =
	| TokenIdentifier
	| TokenOperator
	| TokenSeparator
	| TokenKeyword
	| TokenLiteral
	| TokenEof;

/** Whether the given character is a hex character. */
function isHex(c: string) {
	cafebabeAssert(c.length === 1, "isHex precond");
	return (
		(c >= "0" && c <= "9") || (c >= "a" && c <= "f") || (c >= "A" && c <= "F")
	);
}

/**
 * Result of attempting to parse a bigint at a given span: either a successful parse, or an error
 */
type BigIntParseResult =
	| {
			value: bigint;
			// Number of digits that were not underscores (used by parseHexFloat)
			realDigits: number;
	  }
	| string /* error */;

/**
 * Get the integer, possibly with underscores, with the given base in the given text at the given span.
 * Checks that the literal's digits are in range and that there are no trailing or leading underscores.
 */
function checkedBigInt(
	text: string,
	spanStart: number,
	spanEnd: number,
	base: number
): BigIntParseResult {
	cafebabeAssert(
		base === 10 || base === 16 || base === 2 || base === 8,
		"Unsupported base"
	);
	const bigintBase = BigInt(base);
	if (text[spanStart] === "_" || text[spanEnd - 1] === "_")
		return "Invalid literal: Cannot start or end with underscore"; // e.g. _0 and 0_ are invalid, but 0_0 is ok
	let result = 0n;
	let digitsExcludingUnderscore = 0;
	for (let i = spanStart; i < spanEnd; ++i) {
		if (text[i] === "_") continue;
		digitsExcludingUnderscore++;
		const parsed = Number.parseInt(text[i]!, base);
		if (Number.isNaN(parsed)) return "Invalid literal: Digit out of range";
		result = bigintBase * result + BigInt(parsed);
	}
	return {value: result, realDigits: digitsExcludingUnderscore};
}

/**
 * Parse a decimal float. Note that floats can end in f or F to signify a float32, or d or D to explicitly make it
 * a double. Otherwise, parsing occurs according to JS rules, so we leverage that for the hard work of conversion.
 * @returns Successful or erroneous parse, and the number of characters read.
 */
function parseDecimalFloatSloppy(
	text: string,
	offset: number
): [FloatingPointLiteral | Erroneous, number] {
	let i = offset;
	let hasDigits = false;

	function skipDigits(): [Erroneous, number] | undefined {
		if (text[i] === "_")
			return [
				{type: Misc.erroneous, value: "Digits cannot start with underscore"},
				i + 1,
			];
		while (
			i < text.length &&
			((text[i]! >= "0" && text[i]! <= "9") || text[i]! === "_")
		) {
			hasDigits = true;
			i++;
		}
		if (text[i - 1] === "_")
			return [
				{type: Misc.erroneous, value: "Digits cannot end with underscore"},
				i + 1,
			];
	}

	let error = skipDigits();
	if (error) return error;
	// Skip .
	if (text[i] === ".") ++i;
	error = skipDigits();
	if (error) return error;

	if (!hasDigits)
		return [{type: Misc.erroneous, value: "Invalid floating-point literal"}, i];

	if (text[i] === "e" || text[i] === "E") {
		// read exponent
		++i;
		if (text[i] === "+" || text[i] === "-") ++i; // read optional exponent sign
		hasDigits = false;
		while (i < text.length && text[i]! >= "0" && text[i]! <= "9") {
			// read exponent digits
			hasDigits = true;
			++i;
		}

		if (!hasDigits)
			return [
				{
					type: Misc.erroneous,
					value: "Invalid floating-point literal exponent",
				},
				i,
			];
	}

	const isF32 = text[i] === "f" || text[i] === "F";
	const isExplicitlyDouble = text[i] === "d" || text[i] === "D";

	let val = Number(text.slice(offset, i).replaceAll("_", ""));
	if (isF32 || isExplicitlyDouble) ++i;

	val = isF32 ? Math.fround(val) : val; // Math.fround rounds to nearest f32

	return [
		{
			type: Misc.floatLiteral,
			isHexFloat: false,
			value: val,
			isF32,
			span: {start: offset, end: i},
		},
		i,
	];
}

/**
 * Process escape like \b and return the char code (or erroneous token) and the amount of characters consumed.
 * Preconditions: Text should start with \
 * See section 3.10
 * @param text The text to remove an escape code from
 * @param offset Where to read the escape code from.
 */
export function processCharacterOrOctalEscape(
	text: string,
	offset: number
): [number | Erroneous, number] {
	cafebabeAssert(text[offset] === "\\", "processEscape precondition fail");

	switch (text[offset + 1]) {
		// Allowed escapes are \b, \t, \n, \f, \r, \", \', \\
		case "b":
			return [8, 2];
		case "t":
			return [9, 2];
		case "n":
			return [10, 2];
		case "f":
			return [12, 2];
		case "r":
			return [13, 2];
		case '"':
			return [34, 2];
		case "'":
			return [39, 2];
		case "\\":
			return [92, 2];
		case "0":
		case "1":
		case "2":
		case "3":
		case "4":
		case "5":
		case "6":
		case "7": {
			// octal escape sequence
			let i = offset + 2;
			while (
				i < text.length &&
				i < offset + 4 /* maximum 3 chars */ &&
				text[i]! >= "0" &&
				text[i]! <= "7"
			)
				++i;
			const val = Number.parseInt(text.slice(offset + 1, i), 8);
			if (val > 0xff) {
				// maximum allowed value is 0xff
				return [
					{type: Misc.erroneous, value: "Invalid octal escape sequence"},
					i - offset,
				];
			}
			return [val, i - offset];
		}
		case undefined: // only undefined if at EOF
			return [{type: Misc.erroneous, value: "Unterminated escape sequence"}, 1];
		default: // eat at least one character following \
			return [{type: Misc.erroneous, value: "Invalid escape sequence"}, 2];
	}
}

const charIndicatesFloat: Set<string | undefined> = new Set([...".pPeEfFdD"]);

export type LexerOptions = {
	javaVersion?: JavaVersion;
};

/**
 * Java 8 lexer. Maximal munch (e.g. for type contexts, Cow<Chicken<Pig<Ox>>> will get a GTGTGT token at the end that
 * the type parser must break down. Invoked first to create a list of tokens. Then you can create a LexerContext to
 * walk the tokens with arbitrary lookahead.
 */
export class Lexer {
	public readonly originalText: string;
	// Text after Unicode escape codes are removed. Because this unescaping process shifts things around, spans must
	// be fixed up at the end of lexing.
	private _unescapedResult: UnicodeUnescapedResult;
	// Text to lex (post-escape)
	private _text: string;
	// Current offset in _text (but after escaping)
	public offset = 0;

	// List of accumulated errors
	public errors: CompileError[] = [];

	// Map between token (or comment) and the comment that precedes it. Useful for assembling docstrings
	public commentMap: Map<Comment | Token, Comment> = new Map();
	// Most recent comment
	private _lastComment: Comment | null = null;
	// List of all comments
	public comments: Comment[] = [];

	private readonly options: Required<LexerOptions>;

	constructor(text: string, options: LexerOptions = {}) {
		this.options = {
			javaVersion: 8,
			...options,
		};
		this.originalText = text;
		this._unescapedResult = replaceUnicodeEscapes(text);
		this._text = this._unescapedResult.result;
	}

	/**
	 * Fix up all the error spans so that they are relative to the original coordinates (due to Unicode unescapes).
	 */
	finish() {
		this.errors.forEach((e) => {
			this._unescapedResult.fixupSpanInPlace(e.span);
		});
		this.errors.push(...this._unescapedResult.errors);
	}

	/**
	 * Consume a string literal. Preconditions: The remaining text's first character should be a double quote.
	 * This will never fail in the sense that a string literal will always be returned, as the string is consumed
	 * maximally and errors are handled by substituting bad escapes by ?, or terminating the string early if there is
	 * a newline.
	 */
	nextString(): StringLiteral {
		const text = this._text;
		cafebabeAssert(text[this.offset] === '"', "nextString precondition");

		const INVAL = "?"; // character substituted for invalid escape codes

		let i = this.offset + 1;
		let result = "";
		while (i < text.length) {
			const start = this.offset;
			const c = text[i];
			switch (c) {
				case "\\":
					const [val, consumed] = processCharacterOrOctalEscape(text, i);
					if (typeof val === "number") {
						result += String.fromCharCode(val);
					} else {
						this.error(val.value, i, i + consumed);
					}
					i += consumed;
					result += typeof val === "number" ? String.fromCharCode(val) : INVAL;
					continue;
				case '"': // Found end of string
					this.skipTo(i + 1 /* skip read chars + string terminator */);
					return {
						type: Misc.string,
						value: result,
						span: {start, end: i + 1},
					};
				case "\n":
					this.error("Unterminated string literal", start, i);
					this.skipTo(i);
					return {
						type: Misc.string,
						value: result,
						span: {start, end: i},
					};
				default:
					result += c;
			}
			++i;
		}

		// Only here if we didn't finish parsing
		this.error("Unterminated string literal", this.offset, i);
		const span = {start: this.offset, end: i};
		this.skipTo(i);

		return {
			type: Misc.string,
			value: result,
			span,
		};
	}

	/**
	 * Skip all whitespace to the next non-whitespace character or end of file
	 */
	skipWhitespace(): string {
		let i = this.offset;
		while (i < this._text.length && /\s/.test(this._text[i]!)) ++i;
		if (i !== this.offset) this.skipTo(i);
		return this._text;
	}

	/**
	 * Get a line comment
	 */
	nextLineComment(): Comment {
		const text = this._text;
		cafebabeAssert(
			text.startsWith("//", this.offset),
			"nextLineComment precondition"
		);

		const start = this.offset;
		let i = start + 2; // length of //
		while (i < text.length && text[i] !== "\n") ++i;

		this.skipTo(i);
		return {
			type: Misc.comment,
			value: text.slice(start, i),
			span: {
				start: start,
				end: i,
			},
		};
	}

	/**
	 * Get the next comment delimited by /*.
	 */
	nextTradComment(): Comment {
		const text = this._text,
			start = this.offset;
		cafebabeAssert(text.startsWith("/*", start), "nextTradComment fail");

		const i = text.indexOf("*/", start + 2);
		if (i === -1) {
			// missing */
			this.error("Unterminated comment", start, text.length);
			this.skipTo(text.length);
			return {
				type: Misc.comment,
				value: text + "*/", // to avoid confusing comment parsers
				span: {
					start: start,
					end: text.length,
				},
			};
		}

		const comment = text.slice(start, i + 2);
		this.skipTo(i + 2);

		return {
			type: Misc.comment,
			value: comment,
			span: {
				start: start,
				end: i + 2,
			},
		};
	}

	/**
	 * Consume a character literal, e.g. '\b' or '3'. Note that Unicode escapes are processed prior to this function
	 * being called, so literals like '\u0023' do not need special handling.
	 */
	nextCharLiteral(): Token | Erroneous {
		const text = this._text,
			start = this.offset;
		cafebabeAssert(text[start] === "'", "nextCharLiteral precondition");

		this.skip(1); // skip the '

		switch (text[start + 1]) {
			case undefined:
				this.error("Unterminated character literal", this.offset);
				return {
					type: Misc.characterLiteral,
					value: "?",
					span: {start: this.offset - 1, end: this.offset},
				};
			case "\\": {
				const [val, consumed] = processCharacterOrOctalEscape(text, start + 1);
				if (typeof val !== "number") {
					this.error(val.value, start, start + consumed + 1);
					this.skip(consumed + 1);
					return {
						type: Misc.characterLiteral,
						value: "?",
						span: {start: this.offset - consumed - 1, end: this.offset},
					};
				}

				if (text[start + consumed + 1] !== "'") {
					this.error("Unterminated character literal", start + 1);
					this.skip(consumed + 1);
					return {
						type: Misc.characterLiteral,
						value: "?",
						span: {start: this.offset - consumed - 1, end: this.offset},
					};
				}

				this.skip(consumed + 1);
				return {
					type: Misc.characterLiteral,
					value: String.fromCharCode(val),
					span: {start: this.offset - consumed - 2, end: this.offset},
				};
			}
			case "'":
				this.error("Empty character literal", start, start + 2);
				this.skip(1);
				return {
					type: Misc.characterLiteral,
					value: "?",
					span: {start: this.offset - 1, end: this.offset},
				};
			default:
				if (text[start + 2] !== "'") {
					this.error("Unterminated character literal", start, start + 3);
				}

				this.skip(2);
				return {
					type: Misc.characterLiteral,
					value: text[start + 1]!,
					span: {start: this.offset - 3, end: this.offset},
				};
		}
	}

	/**
	 * Read the next hexfloat.
	 */
	nextHexFloat(): FloatingPointLiteral | Erroneous {
		const result = parseHexFloat(this._text, this.offset);
		const start = this.offset;
		this.skip(result.consumed);

		if (result.success) {
			const {value, conversionWasInexact} = result;
			if (conversionWasInexact) {
				if (value === 0)
					this.error(
						"Floating-point literal underflow",
						start,
						this.offset - 1
					);
				else if (value === Infinity || value === -Infinity)
					this.error("Floating-point literal overflow", start, this.offset - 1);
			}

			return {
				type: Misc.floatLiteral,
				isHexFloat: true,
				value,
				isF32: result.isF32,
				span: {start, end: this.offset},
			};
		} else {
			return {
				type: Misc.erroneous,
				value: result.error,
			};
		}
	}

	/**
	 * Get the next normal, decimal float.
	 */
	nextDecimalFloat(): FloatingPointLiteral | Erroneous {
		// TODO error on underflow/overflow
		const [token, consumed] = parseDecimalFloatSloppy(this._text, this.offset);
		this.skipTo(consumed);
		return token;
	}

	/**
	 * Get the next numeric token: integer or floating point.
	 * Contract: First character must be . or 0 through 9.
	 */
	nextNumeric(): IntegerLiteral | FloatingPointLiteral | Erroneous {
		const text = this._text;
		const start = this.offset;

		cafebabeAssert(
			text[start] === "." || (text[start]! >= "0" && text[start]! <= "9"),
			"nextNumeric precondition"
		);

		if (text[start] === ".") {
			return this.nextDecimalFloat();
		}

		enum Mode {
			HEX_OR_HEXFLOAT, // starts with 0x
			BINARY, // starts with 0b
			OCTAL_OR_DECIMAL_FLOAT, // starts with 0
			DECIMAL_OR_DECIMAL_FLOAT, // starts with . or 1-9
		}

		let parseMode =
			text.startsWith("0x", start) || text.startsWith("0X", start)
				? Mode.HEX_OR_HEXFLOAT
				: text.startsWith("0b", start) || text.startsWith("0B", start)
					? Mode.BINARY
					: text.startsWith("0", start)
						? Mode.OCTAL_OR_DECIMAL_FLOAT
						: Mode.DECIMAL_OR_DECIMAL_FLOAT;

		const digitsStart =
			start +
			(parseMode === Mode.HEX_OR_HEXFLOAT || parseMode === Mode.BINARY ? 2 : 0); // skip 0x/0b prefix
		let digitsEnd = digitsStart;
		let c: string;

		const validChar =
			parseMode === Mode.HEX_OR_HEXFLOAT
				? isHex
				: (char: string) => char >= "0" && char <= "9";
		// Skip digits to detect whether this is a float
		while (
			digitsEnd < text.length &&
			(validChar((c = text[digitsEnd]!)) || c === "_")
		) {
			digitsEnd++;
		}
		if (
			parseMode !== Mode.BINARY /* no such thing as binary float */ &&
			charIndicatesFloat.has(text[digitsEnd])
		)
			return parseMode === Mode.HEX_OR_HEXFLOAT
				? this.nextHexFloat()
				: this.nextDecimalFloat();

		if (digitsStart === digitsEnd) {
			// missing digits after 0x or 0b
			const err =
				parseMode === Mode.HEX_OR_HEXFLOAT
					? "Invalid hex literal"
					: "Invalid binary literal";
			this.skip(2);
			return {
				type: Misc.erroneous,
				value: err,
			};
		} else if (parseMode === Mode.OCTAL_OR_DECIMAL_FLOAT && digitsEnd === 1) {
			// Parsing 0 or 0L, treat as decimal instead of octal to lessen confusion
			parseMode = Mode.DECIMAL_OR_DECIMAL_FLOAT;
		}

		const base =
			parseMode === Mode.BINARY
				? 2
				: parseMode === Mode.HEX_OR_HEXFLOAT
					? 16
					: parseMode === Mode.OCTAL_OR_DECIMAL_FLOAT
						? 8
						: 10;
		const value = checkedBigInt(text, digitsStart, digitsEnd, base);

		const isLongLiteral = text[digitsEnd] === "L" || text[digitsEnd] === "l";
		if (isLongLiteral) ++digitsEnd;
		const span = {start, end: digitsEnd};

		this.skipTo(digitsEnd);
		if (typeof value === "string") {
			return {
				type: Misc.erroneous,
				value: value,
			};
		}

		// to-do: Check overflow which is an error
		return {
			type: Misc.integer,
			value: value.value,
			isLong: isLongLiteral,
			originalType:
				base === 2
					? "binary"
					: base === 8
						? "octal"
						: base === 16
							? "hex"
							: "decimal",
			span,
		};
	}

	nextImpl(): Token | null | Comment | Erroneous {
		const text = this._text,
			offset = this.offset;
		if (offset >= text.length) return null;

		// Emit an operator token.
		const op = (s: Operator | Separator): Token => {
			const skipLen = tokenTypeToLength(s);
			const span = {start: this.offset, end: this.offset + skipLen};
			this.skip(skipLen);
			return {
				type: s,
				span,
			} as TokenOperator | TokenSeparator;
		};

		const s = text[offset]!;
		switch (s) {
			case '"':
				return this.nextString();
			case "'":
				return this.nextCharLiteral();
			case "/": {
				switch (text[offset + 1]) {
					case "/":
						return this.nextLineComment();
					case "*":
						return this.nextTradComment();
					case "=":
						return op(Operator.SlashEq);
					default:
						return op(Operator.Slash);
				}
			}
			case "=": {
				return op(text[offset + 1] === "=" ? Operator.EqEq : Operator.Eq);
			}
			case ">": {
				switch (text[offset + 1]) {
					case ">": {
						switch (text[offset + 2]) {
							case ">": {
								return op(
									text[offset + 3] === "=" ? Operator.GtGtGtEq : Operator.GtGtGt
								);
							}
							case "=": {
								return op(Operator.GtGtEq);
							}
							default:
								return op(Operator.GtGt);
						}
					}
					case "=": {
						return op(Operator.GtEq);
					}
					default:
						return op(Operator.Gt);
				}
			}
			case "<": {
				switch (text[offset + 1]) {
					case "=":
						return op(Operator.LtEq);
					case "<": {
						return op(
							text[offset + 2] === "=" ? Operator.LtLtEq : Operator.LtLt
						);
					}
					default:
						return op(Operator.Lt);
				}
			}
			case "!": {
				return op(text[offset + 1] === "=" ? Operator.NotEq : Operator.Not);
			}
			case "&": {
				switch (text[offset + 1]) {
					case "&":
						return op(Operator.AndAnd);
					case "=":
						return op(Operator.AndEq);
					default:
						return op(Operator.And);
				}
			}
			case "|": {
				switch (text[offset + 1]) {
					case "|":
						return op(Operator.OrOr);
					case "=":
						return op(Operator.OrEq);
					default:
						return op(Operator.Or);
				}
			}
			case "+": {
				switch (text[offset + 1]) {
					case "+":
						return op(Operator.PlusPlus);
					case "=":
						return op(Operator.PlusEq);
					default:
						return op(Operator.Plus);
				}
			}
			case "-": {
				switch (text[offset + 1]) {
					case "-":
						return op(Operator.MinusMinus);
					case "=":
						return op(Operator.MinusEq);
					case ">":
						return op(Operator.Arrow);
					default:
						return op(Operator.Minus);
				}
			}
			case "*":
				return op(text[offset + 1] === "=" ? Operator.StarEq : Operator.Star);
			case "%":
				return op(
					text[offset + 1] === "=" ? Operator.PercentEq : Operator.Percent
				);
			case "^":
				return op(text[offset + 1] === "=" ? Operator.CaretEq : Operator.Caret);
			case "~":
				return op(Operator.Tilde);
			case "?":
				return op(Operator.Question);
			case ":":
				return op(
					text[offset + 1] === ":" ? Separator.ColonColon : Operator.Colon
				);
			case "(":
				return op(Separator.LParen);
			case ")":
				return op(Separator.RParen);
			case "{":
				return op(Separator.LBrace);
			case "}":
				return op(Separator.RBrace);
			case "[":
				return op(Separator.LBracket);
			case "]":
				return op(Separator.RBracket);
			case ";":
				return op(Separator.Semicolon);
			case ",":
				return op(Separator.Comma);
			case ".": {
				if (text.startsWith("...", offset)) {
					return op(Separator.Ellipsis);
				} else if (
					text[offset + 1] !== undefined &&
					text[offset + 1]! >= "0" &&
					text[offset + 1]! <= "9"
				) {
					// Floating-point literal without leading digits
					return this.nextNumeric();
				} else {
					return op(Separator.Dot);
				}
			}
			case "@":
				return op(Separator.At);

			default: {
				// Must be a literal, keyword, or identifier.
				if (s >= "0" && s <= "9") {
					return this.nextNumeric();
				}

				return this.nextKeywordOrIdentifier();
			}
		}
	}

	/**
	 * Get the next Token, or null if there are no more tokens left.
	 */
	next(): Token {
		while (true) {
			const lastComment = this._lastComment;
			this.skipWhitespace();

			const lastOffset = this.offset; // so that token spans don't include whitespace

			const token = this.nextImpl();
			if (token === null) {
				return {type: Misc.eof, span: {start: this.offset, end: this.offset}};
			}

			if (token.type === Misc.erroneous) {
				this.error(token.value, lastOffset, this.offset);
				continue;
			}
			this._unescapedResult.fixupSpanInPlace(token.span);

			if (lastComment !== null) {
				this.commentMap.set(token, lastComment);
			}

			if (token.type === Misc.comment) {
				this.comments.push(token);
				this._lastComment = token;
			} else {
				this._lastComment = null;
				return token;
			}
		}
	}

	/**
	 * Add an error with the given message, in an absolute position. Return an erroneous token
	 * representing the error, and the number of characters consumed.
	 */
	error(errorText: string, start = 0, end = start + 1): [Erroneous, number] {
		end = Math.max(end, start + 1);

		const error = new CompileError(errorText, {start: start, end});
		this.errors.push(error);

		return [
			{
				type: Misc.erroneous,
				value: this.originalText.slice(start, end),
			},
			end - start,
		];
	}

	private nextKeywordOrIdentifier():
		| TokenIdentifier
		| TokenKeyword
		| TokenLiteral
		| Erroneous {
		const text = this._text,
			offset = this.offset;
		if (isJavaIdentifierStart(text.charCodeAt(offset) || 0)) {
			let i = offset + 1;
			while (i < text.length && isJavaIdentifierPart(text.charCodeAt(i))) ++i;

			const ident = text.slice(offset, i);
			const span = {start: this.offset, end: i};
			this.skipTo(i);

			if (isStringKeywordOrLiteral(ident, this.options.javaVersion)) {
				if (ident === "true" || ident === "false") {
					return {
						type: Misc.booleanLiteral,
						value: ident === "true",
						span,
					};
				}
				return {
					type: ident === "null" ? Misc.null : Reflect.get(Keyword, ident),
					span,
				};
			} else {
				return {
					type: Misc.identifier,
					value: ident,
					span,
				};
			}
		}

		this.skip(1);
		return this.error("Invalid character " + text[offset], offset)[0];
	}

	private skip(consumed: number) {
		cafebabeAssert(consumed !== 0, "skip(0) is a no-op");

		this.offset += consumed;
	}

	private skipTo(newOffset: number) {
		cafebabeAssert(newOffset > this.offset, "skipTo precondition");

		this.offset = newOffset;
	}
}
